// Configuration de la gestion de session
export const SESSION_CONFIG = {
  // Durée d'inactivité avant warning (en millisecondes)
  INACTIVITY_WARNING_TIME: 25 * 60 * 1000, // 25 minutes
  
  // Durée totale avant déconnexion automatique (en millisecondes)
  INACTIVITY_LOGOUT_TIME: 30 * 60 * 1000, // 30 minutes
  
  // Durée de vie du token JWT (doit correspondre au backend)
  TOKEN_LIFETIME: 60 * 60 * 1000, // 1 heure
  
  // Durée avant expiration pour refresh automatique
  TOKEN_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes avant expiration
  
  // Clés de stockage
  STORAGE_KEYS: {
    TOKEN: 'auth_token',
    USER: 'auth_user',
    TOKEN_EXPIRES_AT: 'auth_token_expires_at',
    LAST_ACTIVITY: 'auth_last_activity',
  },
};

// Gestionnaire de stockage sécurisé
export const SecureStorage = {
  setToken(token, expiresIn = SESSION_CONFIG.TOKEN_LIFETIME) {
    const expiresAt = Date.now() + expiresIn;
    localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.TOKEN, token);
    localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString());
    this.updateActivity();
  },

  getToken() {
    const token = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.TOKEN);
    const expiresAt = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.TOKEN_EXPIRES_AT);
    
    if (!token || !expiresAt) {
      return null;
    }

    // Vérifier si le token a expiré
    if (Date.now() > parseInt(expiresAt)) {
      this.clearAuth();
      return null;
    }

    return token;
  },

  setUser(user) {
    localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.USER, JSON.stringify(user));
  },

  getUser() {
    const userStr = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  },

  updateActivity() {
    localStorage.setItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
  },

  getLastActivity() {
    const lastActivity = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.LAST_ACTIVITY);
    return lastActivity ? parseInt(lastActivity) : Date.now();
  },

  getInactivityDuration() {
    return Date.now() - this.getLastActivity();
  },

  isTokenExpiringSoon() {
    const expiresAt = localStorage.getItem(SESSION_CONFIG.STORAGE_KEYS.TOKEN_EXPIRES_AT);
    if (!expiresAt) return false;
    
    const timeUntilExpiry = parseInt(expiresAt) - Date.now();
    return timeUntilExpiry < SESSION_CONFIG.TOKEN_REFRESH_THRESHOLD;
  },

  clearAuth() {
    Object.values(SESSION_CONFIG.STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  },

  // Rafraîchir les données utilisateur depuis l'API
  async refreshUser() {
    try {
      const token = this.getToken();
      if (!token) return null;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        this.setUser(userData);
        return userData;
      }
      
      return null;
    } catch (error) {
      console.error('[SessionManager] Erreur lors du rafraîchissement utilisateur:', error);
      return null;
    }
  },
};

// Hook personnalisé pour la gestion de l'inactivité
export class InactivityManager {
  constructor(onWarning, onLogout) {
    this.onWarning = onWarning;
    this.onLogout = onLogout;
    this.warningTimer = null;
    this.logoutTimer = null;
    this.events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  }

  start() {
    this.resetTimers();
    this.events.forEach(event => {
      document.addEventListener(event, this.handleActivity);
    });
  }

  stop() {
    this.clearTimers();
    this.events.forEach(event => {
      document.removeEventListener(event, this.handleActivity);
    });
  }

  handleActivity = () => {
    SecureStorage.updateActivity();
    this.resetTimers();
  };

  resetTimers() {
    this.clearTimers();

    // Timer pour le warning
    this.warningTimer = setTimeout(() => {
      if (this.onWarning) {
        this.onWarning();
      }
    }, SESSION_CONFIG.INACTIVITY_WARNING_TIME);

    // Timer pour la déconnexion
    this.logoutTimer = setTimeout(() => {
      if (this.onLogout) {
        this.onLogout();
      }
    }, SESSION_CONFIG.INACTIVITY_LOGOUT_TIME);
  }

  clearTimers() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  getRemainingTime() {
    const inactivityDuration = SecureStorage.getInactivityDuration();
    const remainingTime = SESSION_CONFIG.INACTIVITY_LOGOUT_TIME - inactivityDuration;
    return Math.max(0, remainingTime);
  }

  getRemainingTimeFormatted() {
    const remaining = this.getRemainingTime();
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

export default {
  SESSION_CONFIG,
  SecureStorage,
  InactivityManager,
};
